import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  changePasswordResultSchema,
  profileOtpDispatchSchema,
  profileSchema,
  type ChangePasswordResult,
  type Profile,
  type ProfileOtpDispatch,
  type ProfileUpdateBody,
} from './schemas';

export async function getProfile(): Promise<Profile> {
  return profileSchema.parse(await apiClient.get(endpoints.profile.admin));
}

/** Full replace (last-write-wins, §12d). The body is built field by field: the DTO is
 *  `forbidNonWhitelisted`, so a stray `phone`/`role` would 400. Response unread — the modal closes
 *  and every reader refetches through invalidation. */
export async function updateProfile({ firstName, lastName, email }: ProfileUpdateBody) {
  await apiClient.put(endpoints.profile.admin, { firstName, lastName, email });
}

export async function sendPasswordOtp(): Promise<ProfileOtpDispatch> {
  return profileOtpDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePassword.sendOtp),
  );
}

export async function resendPasswordOtp(referenceId: string): Promise<ProfileOtpDispatch> {
  return profileOtpDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePassword.resendOtp, { referenceId }),
  );
}

/** Response unread: success means the server now holds the single-use gate ticket 06's PUT spends. */
export async function verifyPasswordOtp(body: { referenceId: string; otp: string }) {
  await apiClient.post(endpoints.profile.changePassword.verifyOtp, body);
}

/** Spends the verify-otp gate. `{ newPassword }` only — Confirm never goes on the wire (§10a). */
export async function changePassword(newPassword: string): Promise<ChangePasswordResult> {
  return changePasswordResultSchema.parse(
    await apiClient.put(endpoints.profile.changePassword.update, { newPassword }),
  );
}

export async function sendCurrentPhoneOtp(): Promise<ProfileOtpDispatch> {
  return profileOtpDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePhone.currentSendOtp),
  );
}

export async function resendCurrentPhoneOtp(referenceId: string): Promise<ProfileOtpDispatch> {
  return profileOtpDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePhone.currentResendOtp, { referenceId }),
  );
}

/** Response unread: success means the server now holds the Step-1 gate (context.md). */
export async function verifyCurrentPhoneOtp(body: { referenceId: string; otp: string }) {
  await apiClient.post(endpoints.profile.changePhone.currentVerifyOtp, body);
}

/** Step 2: `{ phone }` is the bare 10 digits (context.md). The dispatch's `maskedPhone` is the NEW
 *  number's last four, with a fresh 5 resends. */
export async function sendNewPhoneOtp(phone: string): Promise<ProfileOtpDispatch> {
  return profileOtpDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePhone.newSendOtp, { phone }),
  );
}

export async function resendNewPhoneOtp(referenceId: string): Promise<ProfileOtpDispatch> {
  return profileOtpDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePhone.newResendOtp, { referenceId }),
  );
}

/** Step 3. Response unread: success revokes every session and returns no tokens (context.md). */
export async function verifyNewPhoneOtp(body: { referenceId: string; otp: string }) {
  await apiClient.post(endpoints.profile.changePhone.newVerifyOtp, body);
}
